import type { ReactNode } from 'react';
import { PortalPage, PortalPageHeader } from '../portal';

interface SettingsPageProps {
  /** 32px lucide glyph matching the settings nav row. */
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Header-right slot, normally the page's primary Save button. */
  actions?: ReactNode;
  /** Explanatory side column; never put the primary action in it. */
  rail?: ReactNode;
  children: ReactNode;
}

/**
 * Frame shared by every settings page: one `PortalPage` + `PortalPageHeader`,
 * a 640px main column and an optional 320px rail that drops below the main
 * column under `xl`. Always keep it mounted and swap skeleton/content inside
 * so `pageEnter` never replays when data lands.
 */
export function SettingsPage({
  icon,
  title,
  description,
  actions,
  rail,
  children,
}: SettingsPageProps) {
  return (
    <PortalPage>
      <PortalPageHeader
        icon={icon}
        title={title}
        description={
          description ? (
            // Keep the description on the same measure as the content column.
            <span className="block max-w-[640px]">{description}</span>
          ) : undefined
        }
        actions={actions}
      />
      <div className="flex w-full flex-col gap-10 xl:flex-row xl:items-start xl:gap-12">
        <div className="flex w-full min-w-0 max-w-[640px] flex-col gap-6">
          {children}
        </div>
        {rail ? (
          <aside className="flex w-full max-w-[640px] flex-col gap-4 xl:w-[320px] xl:shrink-0">
            {rail}
          </aside>
        ) : null}
      </div>
    </PortalPage>
  );
}
